/**
 * Trimiterea zilnica in EA: starea, ultima rulare si comutatorul (Alexandru, 12.10.2026, #812).
 *
 * Sta pe ecran, deasupra listei „De trimis în EA", nu ascunsa in Setari: e singurul lucru automat
 * care scrie in contabilitate, deci trebuie sa se vada oricand daca e pornit si ce a facut azi.
 * Pornirea si oprirea cer `fan:post` (admin); contabilitatea vede starea.
 *
 * „Trimite acum" (Alexandru, 13.10.2026) face pe loc ce face rularea de dimineata: tot ce e de
 * trimis din anul curent, prin lotul din coada (`/fan/trimite-lot`), cu aceleasi verificari.
 * Merge si cu trimiterea automata oprita. Ecranul vechi avea trimiterea pe zi; cel refacut (#817)
 * pastrase doar selectia, de cel mult 200 — coada de 181 din 13.10 n-ar fi plecat dintr-o data.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Button } from '../../../components/ui/Button'
import { useConfirmare } from '../../../components/ui/Confirmare'
import { Pastila } from '../../../components/ui/Pastila'
import { type FanStatus, lei, mesajServer } from './tipuri'

/** Ce intoarce `GET /fan/trimite-lot/previzualizare`. */
interface PrevizualizareLot {
  total: { n: number; suma: number }
  deschise: { n: number; suma: number }
  inchise: { n: number; suma: number }
  partiale: { n: number; suma: number }
}

/** Ca rularea de dimineata: viramentele din anul curent. Cele din anii trecuti cer data notei. */
function inceputulAnului(): string {
  return `${new Date().getFullYear()}-01-01`
}

export function TrimitereAutomata() {
  const qc = useQueryClient()
  const editabil = useAuthStore((s) => s.permissions).includes('fan:post')
  const { confirma } = useConfirmare()
  const { data } = useQuery<FanStatus>({
    queryKey: ['fan', 'status'],
    queryFn: async () => (await api.get('/fan/status')).data,
  })
  const stare = data?.trimitereAutomata
  const comuta = useMutation({
    mutationFn: async (pornita: boolean) =>
      (await api.put('/fan/trimitere-automata', { pornita })).data as { pornita: boolean },
    onSuccess: (r) => {
      toast.success(
        r.pornita
          ? `Trimiterea automată e pornită. Rulează zilnic la ${stare?.ora ?? ''}.`
          : 'Trimiterea automată e oprită. Încasările se trimit doar de mână.',
      )
      void qc.invalidateQueries({ queryKey: ['fan', 'status'] })
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Schimbarea n-a reușit.')),
  })
  const [seVerifica, setSeVerifica] = useState(false)
  const trimiteAcum = useMutation({
    mutationFn: async () =>
      (await api.post('/fan/trimite-lot', { from: inceputulAnului() })).data as {
        queued: boolean
        candidati: number
        message?: string
      },
    onSuccess: (r) => {
      if (!r.queued) {
        toast.info(r.message ?? 'Nimic de trimis.')
        return
      }
      toast.success(
        `Am pornit trimiterea: ${r.candidati} încasări. Pleacă una câte una; numărul de pe card scade pe măsură ce intră în EA.`,
      )
      // Lotul ruleaza in coada, cate o nota la 0,4 s si cu raspunsul EA — reimprospatam ecranul
      // de cateva ori, cat sa se vada coborand.
      for (const s of [10, 30, 60, 120, 240]) {
        setTimeout(() => void qc.invalidateQueries({ queryKey: ['fan'] }), s * 1000)
      }
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Trimiterea n-a pornit.')),
  })
  if (!stare) return null

  const r = stare.ultimulRezultat
  let ultima = ''
  if (stare.ultimaLa && r) {
    const cand = new Date(stare.ultimaLa).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    ultima = r.eroare
      ? `Ultima rulare (${cand}) n-a trimis nimic: ${r.eroare}`
      : `Ultima rulare (${cand}): ${r.trimise ?? 0} trimise` +
        ((r.esuate ?? 0) > 0 ? `, ${r.esuate} respinse de EA` : '') +
        ((r.partiale ?? 0) > 0 ? `, ${r.partiale} de verificat de mână` : '') +
        ((r.dejaIncasate ?? 0) > 0 ? `, ${r.dejaIncasate} erau deja încasate` : '') +
        '.'
  }

  const intreaba = async () => {
    const ok = await confirma(
      stare.pornita
        ? {
            titlu: 'Oprești trimiterea automată?',
            mesaj: 'Încasările de trimis vor aștepta trimiterea de mână.',
            buton: 'Oprește',
          }
        : {
            titlu: 'Pornești trimiterea automată în ExpertAccounts?',
            mesaj: (
              <div className="grid gap-2">
                <p>
                  Zilnic la {stare.ora} se trimit toate încasările de trimis din anul curent, FAN și
                  Sameday, cu aceleași verificări ca trimiterea de mână. Notele nu se pot anula din
                  CRM.
                </p>
                <p>
                  Facturile încasate parțial, cele nepotrivite și viramentele din anii trecuți rămân
                  pentru trimiterea de mână.
                </p>
              </div>
            ),
            buton: 'Pornește',
            pericol: true,
          },
    )
    if (ok) comuta.mutate(!stare.pornita)
  }

  const intreabaTrimiteAcum = async () => {
    setSeVerifica(true)
    let p: PrevizualizareLot
    try {
      // Intreaba EA ce facturi mai sunt deschise — poate dura pana la 2 minute (#810).
      p = (
        await api.get('/fan/trimite-lot/previzualizare', { params: { from: inceputulAnului() } })
      ).data
    } catch (e) {
      toast.error(mesajServer(e, 'Nu am putut verifica facturile în EA. Nu s-a trimis nimic.'))
      return
    } finally {
      setSeVerifica(false)
    }
    if (p.total.n === 0) {
      toast.info('Nu e nimic de trimis.')
      return
    }
    const ok = await confirma({
      titlu: 'Trimiți acum încasările în ExpertAccounts?',
      mesaj: (
        <div className="grid gap-2">
          <p>
            De trimis: <b>{p.total.n}</b> încasări, {lei(p.total.suma)} lei, din anul curent. Am
            întrebat EA chiar acum:
          </p>
          <ul className="list-disc pl-5">
            <li>
              <b>{p.deschise.n}</b> se trimit ({lei(p.deschise.suma)} lei);
            </li>
            {p.inchise.n > 0 && (
              <li>
                <b>{p.inchise.n}</b> erau deja încasate în EA — nu se mai trimit, ies din listă;
              </li>
            )}
            {p.partiale.n > 0 && (
              <li>
                <b>{p.partiale.n}</b> sunt pe facturi încasate doar parțial — nu se trimit, rămân de
                verificat de mână.
              </li>
            )}
          </ul>
          <p>
            Notele nu se pot anula din CRM. Viramentele din anii trecuți rămân pentru trimiterea cu
            dată impusă.
          </p>
        </div>
      ),
      buton: p.deschise.n > 0 ? `Trimite ${p.deschise.n}` : 'Scoate-le din listă',
      pericol: true,
    })
    if (ok) trimiteAcum.mutate()
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-b4l-border px-4 py-3 text-sm">
      <span className="text-b4l-text-muted">Trimitere automată în EA:</span>
      <Pastila ton={stare.pornita ? 'verde' : 'gri'}>
        {stare.pornita ? `Pornită, zilnic la ${stare.ora}` : 'Oprită'}
      </Pastila>
      {ultima && (
        <span className={`text-xs ${r?.eroare ? 'text-b4l-red-dark' : 'text-b4l-text-muted'}`}>
          {ultima}
        </span>
      )}
      {editabil && (
        <span className="ml-auto flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={seVerifica || trimiteAcum.isPending}
            onClick={intreabaTrimiteAcum}
            title="Trimite acum tot ce e de trimis, fără să aștepți rularea de dimineață"
          >
            {seVerifica ? 'Verific în EA…' : 'Trimite acum'}
          </Button>
          <Button variant="secondary" loading={comuta.isPending} onClick={intreaba}>
            {stare.pornita ? 'Oprește' : 'Pornește'}
          </Button>
        </span>
      )}
    </div>
  )
}
